import React, { useEffect, useRef, useState } from "react";
import { useTabs } from "../../hooks/useTabs";
import { listen } from "../../hooks/Listener";
import { pubCall } from "../../utils/PubSub";
import { KBD } from "../../utils/Keybindings";
import { ENUMS } from "../../utils/Enums";
import { TRANSACTION_NOTE } from "../../utils/Struct";
import { bm_date, bm_time, showConfirm, } from "../../utils/Utils";
import { showError } from "../../ui/Message";
import VirtualTable from "../../ui/VirtualTable";
import Input from "../../ui/Input";
import Label from "../../ui/Label";
import Space from "../../ui/Space";
import IconNav from "../../ui/IconNav";
import Status from "../../ui/Status";
import { getSignal } from "../../App";
import { NewIcon, EditIconInv, CheckIcon, DeleteIcon, UndoIcon, InfoIcon, } from "../../utils/Icons";



function buildAddParams(invoice, type, note) {
    return {
        invoice,
        note: note,
        transaction_status: type,
    };
}


function parseLogNote(value) {
    const raw = String(value || "");
    const match = raw.match(/^([^|]*)\|([^|]*)\|([\s\S]*)$/);

    if (!match) {
        return {
            typeLabel: "",
            clerkCode: "",
            noteText: raw,
        };
    }

    return {
        typeLabel: match[1].trim(),
        clerkCode: match[2].trim(),
        noteText: match[3].trim(),
    };
}



const DRAFT_ID = "new";

export default function POSTransactionLog(props) {
    const { controller = "", route = "", invoice = "", callback = () => { }, onUpdated = () => { }, lockTransaction = () => { } } = props;

    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [mode, setMode] = useState("select");
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const busy = useRef(false);
    const mounted = useRef(true);

    const { index, setIndex, update, getVal, editProps, refs, } = useTabs(
        [
            { key: "type", value: "" },
            { key: "note", value: "" },
        ],
        ""
    );

    const types = ENUMS.TRANSACTION_NOTE_TYPES || [];
    const rowKey = "id";

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    function reportError(error) {
        showError({
            error: error?.message || String(error),
        });
    }

    function finishRequest() {
        busy.current = false;
        if (mounted.current) {
            setLoading(false);
        }
    }


    function changeTransactionLock(_lock_status = "") {
        if (busy.current) return;

        const request = lockTransaction;

        busy.current = true;
        setLoading(true);

        request({ invoice: invoice, lock_status: _lock_status }, (_s, _r) => {
            if (!mounted.current) return;

            finishRequest();

            if (!_s) {
                showError(_r);
                return;
            }

            callback({ refresh: true });
        });
    }

    function mine() {
        alert("Mine is not implemented yet.");
    }


    function loadItems(preferredId, fallbackIndex = 0, startNewWhenEmpty = false) {
        busy.current = true;
        setLoading(true);

        pubCall("point_of_sale.getHeaderExtras", { invoice }, (_s, _r) => {
            if (!mounted.current) {
                return;
            }

            finishRequest();

            if (!_s) {
                showError(_r);
                return;
            }

            const rows = (_r?.transaction_log || []).map(row => ({ ...row, ...parseLogNote(row.note), }));

            setItems(rows);
            setLoaded(true);
            setSelected(rows.find(row => row[rowKey] === preferredId) || rows[Math.min(Math.max(fallbackIndex, 0), rows.length - 1)] || null);
            onUpdated(_r);

            if (startNewWhenEmpty && rows.length === 0) {
                update({ type: "", note: "", });
                setMode("new");
                setIndex("type");
            }
        }
        );
    }

    useEffect(() => {
        if (invoice) {
            loadItems(undefined, 0, true);
        }
    }, [invoice]);

    useEffect(() => {
        if (
            !loading &&
            mode !== "select" &&
            window.bm_state.value === route
        ) {
            refs.current[index]?.focus?.();
        }
    }, [mode, index, route, loading]);

    function blurInput() {
        refs.current[index]?.blur?.();
    }

    function returnToSelection() {
        blurInput();
        setMode("select");
        setIndex("");
    }

    function startNew() {
        if (busy.current || !loaded || mode !== "select") {
            return;
        }
        update({ type: "", note: "", });
        setMode("new");
        setIndex("type");
    }

    function startEdit() {
        if (busy.current || !selected || mode !== "select") {
            return;
        }

        update({
            type: selected.type || "",
            note: selected.noteText || ""
        });

        setMode("edit");
        setIndex("note");
    }

    function chooseType(value) {
        if (busy.current) return;

        const key = String(value || "").toLowerCase();

        if (["l", "u", "m"].includes(key)) {
            update("type", "");

            if (key === "l") {
                changeTransactionLock("Y");
            } else if (key === "u") {
                changeTransactionLock("");
            } else {
                changeTransactionLock("M");
            }

            return;
        }

        update("type", value);

        if (
            types.some(item => item.value.toLowerCase() === key)
        ) {
            setIndex("note");
        }
    }

    function moveSelection(step) {
        if (busy.current || mode !== "select" || !items.length) {
            return;
        }

        const currentIndex = items.findIndex(
            row => row[rowKey] === selected?.[rowKey]
        );

        const nextIndex = Math.min(
            Math.max(currentIndex + step, 0),
            items.length - 1
        );

        setSelected(items[nextIndex]);
    }

    function saveNote(noteValue = getVal("note")) {
        if (busy.current || mode === "select") {
            return;
        }

        const isNew = mode === "new";

        const note = String(noteValue || "");
        const type = String(getVal("type") || "").toLowerCase();

        if (!note.trim()) {
            showError({ error: "Enter a note." });
            return;
        }

        if (mode === "new" && !types.some(item => item.value === type)) {
            showError({ error: "Select a valid note type." });
            setIndex("type");
            return;
        }

        let method;
        let params;

        try {
            if (mode === "new") {
                method = "account_receivable.addNoteByInvoice";
                params = buildAddParams(invoice, type, note);
                params.require_clerk_code = true;
            } else {
                method = "account_receivable.editNote";

                const status = types.find(item => item.label.toLowerCase() === String(selected.typeLabel || "").toLowerCase());

                params = {
                    uuid: selected.id,
                    note,
                    transaction_status: status?.value || "",
                    clerk_code: selected.clerkCode || "",
                };
            }
        } catch (error) {
            reportError(error);
            return;
        }

        const preferredId = selected?.[rowKey];
        const fallbackIndex = Math.max(items.findIndex(row => row[rowKey] === preferredId), 0);

        busy.current = true;
        setLoading(true);

        pubCall(method, params, (_s, _r) => {
            if (!mounted.current) { return; }

            if (!_s) {
                finishRequest();
                showError(_r);
                return;
            }

            if (isNew) {
                finishRequest();
                callback({ refresh: true });
                return;
            }


            returnToSelection();
            loadItems(preferredId, fallbackIndex);
        });
    }

    function deleteNote() {
        if (busy.current || mode !== "select" || !selected) {
            return;
        }

        const row = selected;
        const deletedIndex = items.findIndex(
            item => item[rowKey] === row[rowKey]
        );

        showConfirm({
            controller,
            route,
            title: "Confirm",
            defaultYes: false,
            description: (
                <div style={{ textAlign: "center" }}>
                    Delete this enty?
                </div>
            ),
            callback: confirmed => {
                if (!confirmed || busy.current) { return; }
                busy.current = true;
                setLoading(true);

                pubCall("account_receivable.deleteNote", { keynum: row.keynum, parent: row.parent, type: row.type, timestamp: row.timestamp, sequence: row.sequence, }, (_s, _r) => {
                    if (!mounted.current) {
                        return;
                    }

                    if (!_s) {
                        finishRequest();
                        showError(_r);
                        return;
                    }

                    loadItems(undefined, deletedIndex);
                }
                );
            },
        });
    }

    function escape() {
        if (busy.current) {
            return;
        }

        if (items.length === 0) {
            callback();
            return;
        }

        if (mode !== "select") {
            returnToSelection();
            return;
        }

        callback();
    }

    const nav = [
        {
            key: KBD.N,
            text: "New",
            icon: <NewIcon />,
            action: startNew,
            disabled: loading || !loaded || mode !== "select",
        },
        {
            key: KBD.E,
            text: "Edit",
            icon: <EditIconInv />,
            action: startEdit,
            disabled: loading || mode !== "select" || !selected,
        },
        {
            key: KBD.PAGE_DOWN,
            text: "Save",
            icon: <CheckIcon />,
            action: () => saveNote(),
            disabled: loading || mode === "select",
        },
        {
            key: KBD.D,
            text: "Delete",
            icon: <DeleteIcon />,
            action: deleteNote,
            disabled: loading || mode !== "select" || !selected,
        },
        {
            key: KBD.ESCAPE,
            text: "Escape",
            icon: <UndoIcon />,
            action: escape,
            disabled: loading,
            right: true,
        },
    ];

    useEffect(() => {
        const actions = [
            ...nav.filter(item => !item.disabled),
            {
                key: KBD.ARROW_DOWN,
                action: () => moveSelection(1),
            },
            {
                key: KBD.ARROW_UP,
                action: () => {
                    if (mode === "new" && index === "note") {
                        setIndex("type");
                    } else {
                        moveSelection(-1);
                    }
                },
            },
            {
                key: KBD.SHIFT_TAB,
                action: () => {
                    if (mode === "new" && index === "note") {
                        setIndex("type");
                    }
                },
            },
        ];

        return listen(controller, route, actions, false, [KBD.ESCAPE, KBD.PAGE_DOWN, KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.TAB, KBD.SHIFT_TAB]);
    }, [controller, route, mode, selected, items, loading, loaded, index, getVal("type"), getVal("note"),]);

    const draftRow = { ...TRANSACTION_NOTE, id: DRAFT_ID, };

    const rows = mode === "new" ? [...items, draftRow] : items;

    function isDraftRow(row) {
        if (mode === "new") {
            return row[rowKey] === DRAFT_ID;
        }

        return (
            mode === "edit" &&
            row[rowKey] === selected?.[rowKey]
        );
    }

    const columns = [
        {
            key: "date",
            title: "Date",
            width: 100,
            render: (_value, row) => row.timestamp ? bm_date(row.timestamp) : "",
        },
        {
            key: "time",
            title: "Time",
            width: 85,
            render: (_value, row) => row.timestamp ? bm_time(row.timestamp) : "",
            grow: true,
        },
        {
            key: "clerkCode",
            title: "Clerk",
            width: 50,
            align: "left",
        },
        {
            key: "noteType",
            title: "Type",
            width: 150,
            grow: false,
            render: (value, row) => {
                if (mode === "new" && isDraftRow(row) && index === "type") {
                    return (
                        <Input
                            {...editProps("type")}
                            className="fill"
                            width={40}
                            maxLength={1}
                            chars={[
                                ...types.flatMap(item => [
                                    item.value.toLowerCase(),
                                    item.value.toUpperCase(),
                                ]),
                                "l", "L",
                                "u", "U",
                                "m", "M",
                            ]}

                            edit={!loading && index === "type"}
                            onChange={chooseType}
                        />
                    );
                }

                const typeValue =
                    mode === "new" && isDraftRow(row)
                        ? getVal("type")
                        : value;

                const typeOption = types.find(item =>
                    mode === "new" && isDraftRow(row)
                        ? item.value.toLowerCase() ===
                        String(getVal("type") || "").toLowerCase()
                        : item.label.toLowerCase() ===
                        String(row.typeLabel || "").toLowerCase()
                );

                return (
                    <span style={{ color: typeOption?.color || "inherit" }}>
                        {typeOption?.label || row.typeLabel || ""}
                    </span>
                )

                return (
                    <span style={{ color: typeOption?.color || "inherit" }}>
                        {typeOption?.label || typeValue}
                    </span>
                );
            },
        },
        {
            key: "noteText",
            title: "Note",
            align: "left",
            width: 446,
            grow: false,
            render: (value, row) => {
                if (isDraftRow(row)) {
                    return (
                        <Input
                            {...editProps("note")}
                            className="fill"
                            width={434}
                            edit={!loading && index === "note"}
                        />
                    );
                }

                return value;
            },
        },
    ];

    return (
        <div style={{ width: "800px" }}>
            <div className="shim" />


            <div className="shim" />

            {mode === "new" && index === "type" && (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px", padding: "5px 10px", }} >
                        {[types.slice(0, 13), types.slice(13)].map(
                            (column, columnIndex) => (
                                <div key={columnIndex} style={{ display: "grid", gridTemplateColumns: "26px 100px 12px minmax(0, 1fr)", alignItems: "center", alignContent: "start", gap: "2px 8px", }} >
                                    {column.map(item => (
                                        <React.Fragment key={item.value}>
                                            <kbd
                                                onClick={() => { if (!busy.current) { chooseType(item.value); } }}
                                                className="small"
                                                style={{ color: item.color || "#ffffff", fontFamily: "inherit", fontWeight: 600, textAlign: "center", }}
                                            >
                                                {item.value}
                                            </kbd>
                                            <Label style={{ color: item.color || "#ffffff", whiteSpace: "nowrap", textAlign: "left", }} >
                                                {item.label}
                                            </Label>
                                            <Label style={{ color: item.color || "#ffffff", opacity: 0.7, textAlign: "center", }} >
                                                -
                                            </Label>
                                            <Label style={{ color: item.color || "#ffffff", opacity: 0.7, textAlign: "left", }} >
                                                {item.desc}
                                            </Label>
                                        </React.Fragment>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                    <div className="shim" />
                </>
            )}

            <VirtualTable
                controller={controller}
                route={route}
                row_key={rowKey}
                rounded
                data={rows}
                columns={columns}
                current={mode === "new" ? draftRow : selected}
                align="auto"
                loading={loading}
                height={260}
                offset={0}
                next={moveSelection}
                prev={(count = 1) => moveSelection(-count)}
                onRowClick={row => {
                    if (mode === "select" && !busy.current) {
                        setSelected(row);
                    }
                }}
            />

            {!loading && loaded && !rows.length && (
                <Space>
                    <Label>No transaction log entries. Press N to add one.</Label>
                </Space>
            )}

            {(mode === "new" && index === "type") &&
                <>
                    <div className="shim" /><div className="shim" />
                    <Status type="active"><InfoIcon />[L]ock [U]nlock [M]ine or choose from the menu.</Status>
                </>
            }
            <div className="shim" /><div className="shim" />
            <Space>
                <IconNav nav={nav} />
            </Space>

        </div>
    );
}